import {
    Box,
    ListItem,
    ListItemButton,
    Typography,
    useMediaQuery
} from '@mui/material';
import UserAvatar from './UserAvatar';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { GAS_PATH, ELECTRICITY_PATH, COMMON_PATH } from '@utils';

// helper to format both UNIX‐seconds or ISO strings
const formatTime = (value) => {
    const date = /^\d+$/.test(value)
        ? new Date(Number(value) * 1000)
        : new Date(value);
    return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: 'numeric'
    });
};

export const ChatCard = ({
    imageURL = 'logo192.png',
    userId = 'defaultUserId',
    name = 'Anonymous',
    message = 'No message available',
    time = '',
    messageCount = 0,
    isActive = false,
    isGroup = false
}) => {
    if (messageCount > 9) {
        messageCount = '9+';
    }

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get('tab') || 'all';
    const isSmallScreen = useMediaQuery('(max-width: 1080px)');

    return (
        <ListItem disablePadding>
            <ListItemButton
                component={Link}
                sx={{
                    display: 'flex',
                    flexGrow: '1',
                    py: '12px',
                    px: isSmallScreen ? '12px' : '16px'
                }}
                to={`${
                    location.pathname.includes('gas')
                        ? `${GAS_PATH.CHAT}${
                              isGroup ? '/group' : ''
                          }/${userId}?tab=${tab}`
                        : location.pathname.includes('electricity')
                        ? `${ELECTRICITY_PATH.CHAT}${
                              isGroup ? '/group' : ''
                          }/${userId}?tab=${tab}`
                        : `${COMMON_PATH.CHAT}${
                              isGroup ? '/group' : ''
                          }/${userId}?tab=${tab}`
                }`}
            >
                <span className="relative group">
                    <UserAvatar
                        fullName={name}
                        imgSource={imageURL}
                        isActive={isActive}
                    />
                    <span className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 shadow-lg z-50">
                        {name}
                    </span>
                </span>

                {/* TESTING: Remove screen size restriction - show details on all screen sizes */}
                <Box
                    sx={{
                        flexGrow: 1,
                        ml: '8px',
                        justifyContent: 'space-between',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 'medium',
                            maxWidth: isSmallScreen ? '80px' : '130px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {name}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.75rem',
                            maxWidth: isSmallScreen ? '84px' : '124px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {message || `Say hi!`}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-end'
                    }}
                >
                    <Typography
                        sx={{ fontSize: isSmallScreen ? '0.55rem' : '0.6rem' }}
                    >
                        {time && formatTime(time)}
                    </Typography>
                    {/* TESTING: Always show message count indicator */}
                    <Box
                        sx={{
                            height: messageCount >= 1 ? '24px' : '0px',
                            width: messageCount >= 1 ? '24px' : '0px',
                            bgcolor: 'green',
                            borderRadius: '16px',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            // TESTING: Add border for visibility
                            border: '1px solid #ccc'
                        }}
                    >
                        {/* TESTING: Show messageCount or 0 for debugging */}
                        <Typography
                            sx={{
                                textAlign: 'center',
                                fontSize: isSmallScreen ? '0.6rem' : '0.7rem',
                                lineHeight: '32px',
                                margin: '0',
                                marginTop: '1.5px',
                                marginRight: '1px',
                                padding: '0'
                            }}
                        >
                            {messageCount || '0'}
                        </Typography>
                    </Box>
                </Box>
            </ListItemButton>
        </ListItem>
    );
};

ChatCard.propTypes = {
    imageURL: PropTypes.string,
    userId: PropTypes.string,
    name: PropTypes.string,
    message: PropTypes.string,
    time: PropTypes.string,
    messageCount: PropTypes.number
};

ChatCard.defaultProps = {
    userId: '',
    name: '',
    message: '',
    time: '',
    messageCount: 0,
    isActive: true
};
